import { useState, useCallback, lazy, Suspense } from "react";
import { useLocation } from "react-router";

import UserTable from "../components/admin/UserTable";
import TagTable from "../components/admin/TagTable";
import StandardTable from "../components/admin/StandardTable";
import StandardRefTable from "../components/admin/StandardRefTable";
import CurrentVersionPanel from "../components/admin/CurrentVersionPanel";
import ChangesPanel from "../components/admin/ChangesPanel";
import BackupsPanel from "../components/admin/BackupsPanel";

// Loaded on demand: the rich text editor is only needed by this tab
const HeadnotePanel = lazy(() => import("../components/admin/HeadnotePanel"));

const sidebarButtonBase =
  "w-full text-left px-4 py-3 text-lg font-medium rounded-lg transition duration-300";
const sidebarButtonActive = "bg-gray-100 text-gray-800";
const sidebarButtonInactive =
  "text-gray-100 hover:bg-cyan-700 hover:text-white";

const AdminPage = () => {
  // Allow other pages to open a specific tab, e.g. navigate("/admin", { state: { tab: "changes" } })
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.tab || "users");
  const [headnoteDirty, setHeadnoteDirty] = useState(false);
  const handleHeadnoteDirty = useCallback((dirty) => setHeadnoteDirty(dirty), []);

  // Ask before leaving the Headnote editor with unsaved changes
  const selectTab = (tab) => {
    if (
      tab !== activeTab &&
      activeTab === "headnote" &&
      headnoteDirty &&
      !window.confirm("The headnote has unsaved changes. Leave anyway?")
    ) {
      return;
    }
    setActiveTab(tab);
  };

  return (
    <div
      className={`flex flex-col md:flex-row ${
        activeTab === "version"
          ? "h-[calc(100vh-4rem)]"
          : "min-h-[calc(100vh-4rem)]"
      }`}
    >
      {/* Sidebar — horizontal on mobile, vertical on md+ */}
      <div className="bg-gradient-to-b from-cyan-900 to-cyan-800 md:w-48 flex-shrink-0">
        {/* Mobile: horizontal tabs */}
        <div className="flex md:hidden p-2 gap-2">
          <button
            onClick={() => selectTab("users")}
            className={`flex-1 text-center px-4 py-2 text-base font-medium rounded-lg transition duration-300 ${
              activeTab === "users"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Users
          </button>
          <button
            onClick={() => selectTab("tags")}
            className={`flex-1 text-center px-4 py-2 text-base font-medium rounded-lg transition duration-300 ${
              activeTab === "tags" ? sidebarButtonActive : sidebarButtonInactive
            }`}
          >
            Tags
          </button>
          <button
            onClick={() => selectTab("standards")}
            className={`flex-1 text-center px-4 py-2 text-base font-medium rounded-lg transition duration-300 ${
              activeTab === "standards"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Standards
          </button>
          <button
            onClick={() => selectTab("standardRefs")}
            className={`flex-1 text-center px-4 py-2 text-base font-medium rounded-lg transition duration-300 ${
              activeTab === "standardRefs"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Stand.-Refs
          </button>
          <button
            onClick={() => selectTab("headnote")}
            className={`flex-1 text-center px-4 py-2 text-base font-medium rounded-lg transition duration-300 ${
              activeTab === "headnote"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Headnote
          </button>
          <button
            onClick={() => selectTab("version")}
            className={`flex-1 text-center px-4 py-2 text-base font-medium rounded-lg transition duration-300 ${
              activeTab === "version"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Version
          </button>
          <button
            onClick={() => selectTab("changes")}
            className={`flex-1 text-center px-4 py-2 text-base font-medium rounded-lg transition duration-300 ${
              activeTab === "changes"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Changes
          </button>
          <button
            onClick={() => selectTab("backups")}
            className={`flex-1 text-center px-4 py-2 text-base font-medium rounded-lg transition duration-300 ${
              activeTab === "backups"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Backups
          </button>
        </div>

        {/* Desktop: vertical sidebar */}
        <div className="hidden md:flex flex-col gap-2 p-3">
          <button
            onClick={() => selectTab("users")}
            className={`${sidebarButtonBase} ${
              activeTab === "users"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Users
          </button>
          <button
            onClick={() => selectTab("tags")}
            className={`${sidebarButtonBase} ${
              activeTab === "tags" ? sidebarButtonActive : sidebarButtonInactive
            }`}
          >
            Tags
          </button>
          <button
            onClick={() => selectTab("standards")}
            className={`${sidebarButtonBase} ${
              activeTab === "standards"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Standards
          </button>
          <button
            onClick={() => selectTab("standardRefs")}
            className={`${sidebarButtonBase} ${
              activeTab === "standardRefs"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Stand.-Refs
          </button>
          <button
            onClick={() => selectTab("headnote")}
            className={`${sidebarButtonBase} ${
              activeTab === "headnote"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Headnote
          </button>
          <button
            onClick={() => selectTab("version")}
            className={`${sidebarButtonBase} ${
              activeTab === "version"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Versions
          </button>
          <button
            onClick={() => selectTab("changes")}
            className={`${sidebarButtonBase} ${
              activeTab === "changes"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Changes
          </button>
          <button
            onClick={() => selectTab("backups")}
            className={`${sidebarButtonBase} ${
              activeTab === "backups"
                ? sidebarButtonActive
                : sidebarButtonInactive
            }`}
          >
            Backups
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div
        className={`flex-1 p-4 flex flex-col ${
          activeTab === "version" ? "overflow-hidden" : ""
        }`}
      >
        {activeTab === "users" && <UserTable />}
        {activeTab === "tags" && <TagTable />}
        {activeTab === "standards" && <StandardTable />}
        {activeTab === "standardRefs" && <StandardRefTable />}
        {activeTab === "headnote" && (
          <Suspense
            fallback={<p className="text-sm text-gray-500">Loading editor…</p>}
          >
            <HeadnotePanel onDirtyChange={handleHeadnoteDirty} />
          </Suspense>
        )}
        {activeTab === "version" && <CurrentVersionPanel />}
        {activeTab === "changes" && <ChangesPanel />}
        {activeTab === "backups" && <BackupsPanel />}
      </div>
    </div>
  );
};

export default AdminPage;
